import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { theme, breakpoint } from '../../../theme';
import OutsideClickHandler from 'react-outside-click-handler';
import { CategoryListItem } from './types';
import { CategoryTile } from './CategoryTile';
import { animated, useSpring } from 'react-spring';
import { CategorySubMenu } from './CategorySubMenu';
import { useBreakpointMap } from '../../../utils';

const CategoryListContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 70px;
  z-index: 4;
  left: 0px;
  width: 100vw;
  box-shadow: 0px 8px 16px ${theme.colors.neutrals.transparentGrays[5]};
  ${breakpoint('md')`
      top: 72px;
      width: 100%;    
  `}
`;

export type CategoryListProps = {
  categories: CategoryListItem[];
  linkComponent?: React.FunctionComponent<PropsWithChildren<{ href: string }>>;
  isOpen: boolean;
  onClose: (e?: MouseEvent) => void;
};

export const CategoryList: React.FC<CategoryListProps> = ({
  categories,
  linkComponent,
  isOpen,
  onClose,
}) => {
  const refMeasure = useRef<HTMLDivElement>(null);
  const [height, s_height] = useState(0);
  const [openCategory, setOpenCategory] = useState<string>('');
  const listTransitions = useSpring({
    to: async (next, _cancel) => {
      await next({ maxHeight: isOpen ? height : 0, overflow: 'hidden' });
      await next({ overflow: isOpen ? 'visible' : 'hidden' });
    },
    from: { maxHeight: 0, overflow: 'hidden' },
  });

  const size = useBreakpointMap({ md: 'desktop' });
  const isDesktop = size === 'desktop';

  useEffect(() => {
    if (!refMeasure.current) return;
    const h = refMeasure.current.getBoundingClientRect().height;
    s_height(h);
  }, [categories, isOpen]);

  return (
    <OutsideClickHandler onOutsideClick={onClose}>
      <CategoryListContainer>
        <animated.div style={listTransitions}>
          <div ref={refMeasure}>
            {categories.map((category) => (
              <span key={`nav_${category.id}`}>
                <CategoryTile
                  category={category}
                  linkComponent={linkComponent}
                  isOpen={openCategory === category.id}
                  onClick={setOpenCategory}
                  onClose={onClose}
                />
                <CategorySubMenu
                  category={category}
                  onClick={setOpenCategory}
                  items={category.subMenuItems}
                  isOpen={openCategory === category.id}
                  isDesktop={isDesktop}
                  onClose={() => {
                    setOpenCategory('');
                    onClose();
                  }}
                  onBack={() => setOpenCategory('')}
                />
              </span>
            ))}
          </div>
        </animated.div>
      </CategoryListContainer>
    </OutsideClickHandler>
  );
};
